'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

export function TopNav() {
  const [scrolled, setScrolled] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > window.innerHeight * 0.85);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Swap the CTA to "Dashboard" when a session exists. Starts false so the
  // server/first-client render matches; the effect flips it after hydration.
  useEffect(() => {
    const supabase = createClient();
    let active = true;

    // getSession() reads the locally-stored session (no network round-trip),
    // so the swap to "Dashboard" happens immediately on load rather than after
    // a /auth/v1/user request — avoids a visible Login→Dashboard flash.
    supabase.auth.getSession().then(({ data }) => {
      if (active) setLoggedIn(!!data.session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (active) setLoggedIn(!!session?.user);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 h-[72px] z-50 flex items-center justify-between px-6 lg:px-12 transition-all duration-300 ${
        scrolled ? 'bg-card/95 backdrop-blur-md border-b border-border-subtle' : 'bg-transparent'
      }`}
    >
      {/* Logo — single image, two color layers crossfade so it stays the same
          logo and fades to the tone needed against the current background */}
      <Link href="/" className="flex items-center group shrink-0 relative h-full">
        {/* Dark layer in flow — defines the box; visible once the background turns white */}
        <Image
          src="/name-and-logo-black-trimmed.png"
          alt="Sentrial Logo"
          width={320}
          height={90}
          className={`object-contain h-9 w-auto origin-left transition-[opacity,transform] duration-300 group-hover:scale-105 ${
            scrolled ? 'opacity-100 dark:opacity-0' : 'opacity-0'
          }`}
        />
        {/* White layer overlaid exactly — visible over the dark hero at the top,
            and kept visible when scrolled in dark mode (dark nav = white logo) */}
        <Image
          src="/name-and-logo-black-trimmed.png"
          alt=""
          aria-hidden="true"
          width={320}
          height={90}
          className={`absolute left-0 top-1/2 -translate-y-1/2 object-contain h-9 w-auto origin-left invert brightness-200 transition-[opacity,transform] duration-300 group-hover:scale-105 ${
            scrolled ? 'opacity-0 dark:opacity-100' : 'opacity-100'
          }`}
        />
      </Link>

      {/* Center Links — absolutely positioned for true centering */}
      <div
        className={`hidden md:flex items-center gap-8 text-body font-medium absolute left-1/2 -translate-x-1/2 transition-colors duration-300 ${
          scrolled ? 'text-text-secondary' : 'text-white/80'
        }`}
        style={{ fontFamily: 'Nunito, sans-serif' }}
      >
        <a
          href="https://sentrial.com/blog"
          className={`transition-colors ${scrolled ? 'hover:text-ink' : 'hover:text-white'}`}
        >
          Blog
        </a>
        <Link
          href="#pricing"
          className={`transition-colors ${scrolled ? 'hover:text-ink' : 'hover:text-white'}`}
        >
          Pricing
        </Link>
        <a
          href="https://sentrial.com/docs"
          target="_blank"
          className={`transition-colors ${scrolled ? 'hover:text-ink' : 'hover:text-white'}`}
        >
          Docs
        </a>
      </div>

      {/* Right Buttons */}
      <div
        className="flex items-center gap-4 shrink-0"
        style={{ fontFamily: 'Nunito, sans-serif' }}
      >
        <Link
          href="https://cal.com/neel-sharma/sentrial?overlayCalendar=true"
          target="_blank"
          className={`hidden sm:block text-body font-medium px-5 py-2 rounded-none transition-all duration-300 ${
            scrolled
              ? 'text-ink border border-border-subtle hover:bg-cream-50'
              : 'text-white border border-white/25 dark:border-white/10 hover:bg-card/10'
          }`}
        >
          Book a Call
        </Link>
        <Link
          href={loggedIn ? '/onboarding' : '/login'}
          className="text-body text-white font-medium bg-warm-brown px-5 py-2 rounded-none hover:bg-warm-brown-hover transition-colors flex items-center gap-1.5"
        >
          {loggedIn ? 'Dashboard' : 'Login'}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </nav>
  );
}
